/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { request, requestImage } from '@/api/client';
import { letterheadChanged } from '@/api/invalidate';

/**
 * Amida's company details: held once, and read wherever they are shown.
 *
 * Every document Vellum makes carries them, so saving them publishes a new
 * version of the paper documents are made on. What was already made keeps
 * what it was made with.
 *
 * There are two logos: the one at the top of every document, and the one in
 * Vellum itself (the rail, the top bar, the sign-in page). They are different
 * images, and replacing one leaves the other as it was.
 */
export const organizationSchema = z.object({
  name: z.string(),
  address: z.string(),
  city: z.string(),
  phone: z.string(),
  fax: z.string(),
  website: z.string(),
  /** Null until somebody saves them; they are then read off the documents' paper. */
  version: z.number().int().nullable(),
  updated_at: z.string().nullable(),
  has_logo: z.boolean(),
  /** Changes whenever the logo on documents does. */
  logo_tag: z.string().nullable(),
  /** Changes whenever the app's logo does; null until one is uploaded. */
  app_logo_tag: z.string().nullable(),
  letterhead_version: z.number().int().nullable(),
  /** Logo position on documents: 'left' or 'right'. */
  logo_position: z.enum(['left', 'right']),
});

export type Organization = z.infer<typeof organizationSchema>;

/** Which logo: the one on documents, or the one in the app. */
export type LogoTarget = 'documents' | 'app';

/** The parts a person edits. */
export type Details = Pick<
  Organization,
  'name' | 'address' | 'city' | 'phone' | 'fax' | 'website' | 'logo_position'
>;

export const organizationQuery = () =>
  queryOptions({
    queryKey: ['organization'] as const,
    queryFn: ({ signal }) =>
      request('/api/v1/organization', organizationSchema, { signal }),
  });

/**
 * The logo uploaded for the app, or null when there is none.
 *
 * Public, so the sign-in page can show it before anybody has signed in -- a
 * company's logo is not protected information. Fetched once as the image
 * itself and handed out as a local address for it, so the rail, the top bar
 * and the sign-in card share one download. None uploaded is a `204`, not an
 * error -- so no page logs a failed request for it -- and simply means "use
 * the built-in mark". `tag` is the details' `app_logo_tag` when it is known, so a
 * new logo is a new address rather than a cached old one.
 */
export const organizationLogoQuery = (tag: string | null = null) =>
  queryOptions({
    queryKey: ['organization-logo', tag] as const,
    queryFn: async ({ signal }): Promise<string | null> => {
      const version = tag ? `&v=${encodeURIComponent(tag)}` : '';
      const image = await requestImage(
        `/api/v1/organization/logo?for=app&only=uploaded${version}`,
        { signal },
      );
      // A browser without object URLs shows the built-in mark instead.
      if (!image || typeof URL.createObjectURL !== 'function') return null;
      return URL.createObjectURL(image);
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

/** The line the contact details make along the bottom of a document. */
export function footerLine(parts: Omit<Details, 'name'>): string {
  const pieces = [
    parts.address.trim(),
    parts.city.trim(),
    parts.phone.trim() ? `P: ${parts.phone.trim()}` : '',
    parts.fax.trim() ? `F: ${parts.fax.trim()}` : '',
    parts.website.trim(),
  ];
  return pieces.filter(Boolean).join(' | ');
}

/** Save the details as a new version. */
export function useSaveDetails() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (details: Details) =>
      request('/api/v1/organization', organizationSchema, {
        method: 'PUT',
        body: details,
      }),
    onSuccess: (fresh) => {
      client.setQueryData(organizationQuery().queryKey, fresh);
      void letterheadChanged(client);
    },
  });
}

/**
 * Replace one of the two logos.
 *
 * For documents, the server publishes a new version of the paper, so the
 * letterhead is refetched. For the app, the logo query is invalidated, so the
 * rail and the top bar show the new one without a reload.
 */
export function useReplaceLogo(target: LogoTarget) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request(`/api/v1/organization/logo?for=${target}`, organizationSchema, {
        method: 'PUT',
        body: form,
      });
    },
    onSuccess: async (fresh) => {
      client.setQueryData(organizationQuery().queryKey, fresh);
      if (target === 'app') {
        await client.invalidateQueries({ queryKey: ['organization-logo'] });
      } else {
        await letterheadChanged(client);
      }
    },
  });
}

/**
 * Where a logo is read from, through Vellum and never from the store
 * (`DOC-6`). `tag` changes only when that logo does, so a browser keeps its
 * copy until then.
 */
export function logoUrl(target: 'documents' | 'app', tag: string): string {
  return `/api/v1/organization/logo?for=${target}&v=${encodeURIComponent(tag)}`;
}
